import type { Size } from "../generated/api/types.gen";

/** A filter's selection after toggling ``value``: added if absent, else removed. */
export function toggleValue(selected: string[], value: string): string[] {
  return selected.includes(value)
    ? selected.filter((item) => item !== value)
    : [...selected, value];
}

/**
 * Selected subcategory IDs to keep after changing the category selection to
 * ``categoryIds``. Clearing every category clears every subcategory; otherwise,
 * only subcategories known (from ``subcategories``) to belong to a deselected
 * category are dropped, so toggling a category before the subcategory list
 * finishes loading doesn't wipe valid subcategory filters from the URL.
 */
export function keepSubcategoriesForCategories(
  subcategoryIds: string[],
  categoryIds: string[],
  subcategories: { id: string; catalog_category_id: string }[],
): string[] {
  if (categoryIds.length === 0) return [];
  const parentBySub = new Map(
    subcategories.map((sub) => [sub.id, sub.catalog_category_id]),
  );
  return subcategoryIds.filter((id) => {
    const parent = parentBySub.get(id);
    return parent === undefined || categoryIds.includes(parent);
  });
}

/** "Landscape Forms (1,021)" — the count grouped for readability. */
export function formatCountLabel({
  name,
  count,
}: {
  name: string;
  count: number;
}): string {
  return `${name} (${count.toLocaleString("en-US")})`;
}

export interface SizePart {
  /** W, D, H, L, or Ø for a diameter. */
  axis: string;
  inches: string;
  centimeters: string;
}

const SIZE_AXES: [keyof Size, string][] = [
  ["diameter_in", "Ø"],
  ["width_in", "W"],
  ["depth_in", "D"],
  ["length_in", "L"],
  ["height_in", "H"],
];

/** "44.0" reads as "44"; anything finer keeps one decimal place. */
function formatMeasure(value: number): string {
  return String(Math.round(value * 10) / 10);
}

/** A size's stated axes in reading order (Ø, W, D, L, H), in inches and cm. */
export function sizeParts(size: Size): SizePart[] {
  return SIZE_AXES.flatMap(([key, axis]) => {
    const inches = size[key];
    return inches
      ? [
          {
            axis,
            inches: formatMeasure(inches),
            centimeters: formatMeasure(inches * 2.54),
          },
        ]
      : [];
  });
}

export interface ProjectMenuItem {
  id: string;
  name: string;
  /** 0 for a top-level project, 1 for its subfolders, and so on. */
  depth: number;
}

/** Projects in menu order: each followed by its subfolders, siblings kept in
 *  the order given. A project whose parent isn't listed shows at the top. */
export function projectMenuItems(
  projects: { id: string; name: string; parent_project_id?: string | null }[],
): ProjectMenuItem[] {
  const ids = new Set(projects.map((p) => p.id));
  const children = new Map<string | null, typeof projects>();
  for (const project of projects) {
    const parent =
      project.parent_project_id && ids.has(project.parent_project_id)
        ? project.parent_project_id
        : null;
    children.set(parent, [...(children.get(parent) ?? []), project]);
  }
  const items: ProjectMenuItem[] = [];
  const visit = (parent: string | null, depth: number) => {
    for (const { id, name } of children.get(parent) ?? []) {
      items.push({ id, name, depth });
      visit(id, depth + 1);
    }
  };
  visit(null, 0);
  return items;
}
